/// <amd-module name="Feed/Legacy/Helpers/UpstreamHelper" />
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { IPickListItem } from "VSSUI/PickList";
import { IFilterItemState, IFilterState } from "VSSUI/Utilities/Filter";

import { PackageFilterBarConstants } from "Feed/Common/Constants/Constants";
import { UpstreamSource, UpstreamSourceType } from "Feed/Common/Contracts/Feed";
import { UpstreamHealthStatus, WebPageConstants } from "Feed/Common/Contracts/WebPage";
import * as PackageResources from "Feed/Common/Resources";
import { BowtieIconProps } from "Feed/Common/Utils/Icons";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { IPackageProtocol } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { ISupportedFeatures } from "Feed/Legacy/Protocols/Common/ISupportedFeatures";
import { IFeedProtocolProvider } from "Feed/Legacy/Protocols/ProtocolProvider";
import { IUpstreamSettingsRowData } from "Feed/Legacy/Types/IUpstreamSettingsRowData";
import { sourceKeyToUpstreamSource } from "Feed/Legacy/Utils/Upstream";
import { Feed, UpstreamSource_All, UpstreamSource_Local } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

function getProtocolProvider(pageContext: IVssPageContext): IFeedProtocolProvider {
    return pageContext.getService<IFeedProtocolProvider>("IFeedProtocolProvider");
}

export function filterDefaultUpstreamSources(pageContext: IVssPageContext, feed?: Feed): UpstreamSource[] {
    const webPageDataService = pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
    const defaultUpstreamSources = webPageDataService.getDefaultPublicUpstreamSources();

    return filterUpstreamSources(pageContext, defaultUpstreamSources, feed);
}

export function filterUpstreamSources(
    pageContext: IVssPageContext,
    upstreamSources: UpstreamSource[],
    feed?: Feed
): UpstreamSource[] {
    return upstreamSources.filter(upstreamSource => {
        const protocol = getProtocolProvider(pageContext).get(upstreamSource.protocol);
        if (protocol != null) {
            const supportedFeatures: ISupportedFeatures = protocol.supportedFeatures(feed);
            return supportedFeatures.upstreams == true;
        }

        return false;
    });
}

export function getUpstreamSourceRowData(
    pageContext: IVssPageContext,
    feed: Feed,
    upstreamHealthStatuses: UpstreamHealthStatus[]
): {
    protocolFilterValues: IPackageProtocol[];
    upstreamSources: IUpstreamSettingsRowData[];
} {
    const protocolsSeen = new Set<string>();
    const protocolList: IPackageProtocol[] = [];

    const invalidProtocol: IPackageProtocol = {
        name: "Unknown",
        key: "Unknown",
        vssIconProps: new BowtieIconProps("status-warning"),
        supportedFeatures: (): ISupportedFeatures => {
            return { upstreams: false, projectScopedFeeds: false };
        }
    } as IPackageProtocol;

    const upstreamSourceTypeMap = {};
    upstreamSourceTypeMap[UpstreamSourceType.Public] = PackageResources.UpstreamSourceType_Public;
    upstreamSourceTypeMap[UpstreamSourceType.Internal] = Utils_String.format(
        PackageResources.UpstreamSourceType_Internal,
        PackageResources.AzureArtifacts
    );

    const nonDeletedUpstreamSources = feed.upstreamSources.filter(
        (upstreamSource: UpstreamSource) => upstreamSource.deletedDate == null
    );
    const rowData = nonDeletedUpstreamSources.map((source: UpstreamSource) => {
        const protocol = getProtocolProvider(pageContext).get(source.protocol) || invalidProtocol;

        if (!protocolsSeen.has(protocol.name)) {
            protocolsSeen.add(protocol.name);
            protocolList.push(protocol);
        }

        const upstreamHealthStatus: UpstreamHealthStatus = upstreamHealthStatuses
            ? upstreamHealthStatuses.find(status => status.upstreamId === source.id)
            : null;

        return {
            isInvalid: protocol.supportedFeatures(feed).upstreams == false,
            iconProps: protocol.vssIconProps,
            protocolName: protocol.name,
            upstreamSource: source,
            upstreamSourceType: upstreamSourceTypeMap[source.upstreamSourceType],
            upstreamHealthStatus
        } as IUpstreamSettingsRowData;
    });

    return {
        protocolFilterValues: protocolList,
        upstreamSources: rowData
    };
}

/**
 * Give list of upstream sources that needs to be displayed in grid
 * @param upstreamSourcesInFeed - Initial set of upstream sources set on feed
 * @param upstreamSourcesToAdd - User wants to add these upstream sources
 * @param upstreamSourcesToRemove - User wants to remove these upstream sources
 */
export function mergeUpstreamSources(
    upstreamSourcesInFeed: UpstreamSource[],
    upstreamSourcesToAdd: UpstreamSource[],
    upstreamSourcesToRemove: UpstreamSource[]
): UpstreamSource[] {
    // get list of names to remove
    const upstreamSourcesToRemoveMap: { [key: string]: string } = {};
    for (const upstreamSource of upstreamSourcesToRemove) {
        upstreamSourcesToRemoveMap[upstreamSource.name] = upstreamSource.name;
    }

    const upstreamSources: UpstreamSource[] = [];

    // remove existing upstream sources
    for (const upstreamSource of upstreamSourcesInFeed) {
        if (upstreamSourcesToRemoveMap[upstreamSource.name] == null && upstreamSource.deletedDate == null) {
            upstreamSources.push(upstreamSource);
        }
    }

    // add new upstream sources
    return upstreamSources.concat(...upstreamSourcesToAdd);
}

export function filterSourceKeyToUpstreamSource(sourceKey: string, upstreamSources: UpstreamSource[]): UpstreamSource {
    switch (sourceKey) {
        case null:
            return UpstreamSource_All;
        case WebPageConstants.DirectUpstreamSourceIdForThisFeedFilter:
            return UpstreamSource_Local;
        default:
            const stringEvaluationCallback = (upstreamSource, source) =>
                Utils_String.equals(upstreamSource.id, source, true);
            return sourceKeyToUpstreamSource(sourceKey, upstreamSources, stringEvaluationCallback);
    }
}

export function filterStateToUpstreamSource(
    filterState: IFilterState,
    upstreamSources: UpstreamSource[]
): UpstreamSource {
    const filterSourceState: IFilterItemState = filterState[PackageFilterBarConstants.SourceFilterKey];

    // if filterbar Clear was used, filterState will be null
    // if dropdown Clear was used, value will be empty
    if (filterSourceState != null && filterSourceState.value.length > 0) {
        const directUpstreamSourceId = filterSourceState.value[0].key;

        return filterSourceKeyToUpstreamSource(directUpstreamSourceId, upstreamSources);
    }

    return null;
}

export function upstreamSourceToPickListItem(
    pageContext: IVssPageContext,
    upstreamSource: UpstreamSource
): IPickListItem {
    const icon = getProtocolProvider(pageContext).get(upstreamSource.protocol).vssIconProps;
    const label = Utils_String.format("{0} {1}", upstreamSource.protocol, upstreamSource.name);
    return {
        key: upstreamSource.id, // directUpstreamSourceId
        name: upstreamSource.name,
        iconProps: icon,
        ariaLabel: label
    } as IPickListItem;
}

export function getProtocolFromUpstreamSource(feed: Feed): string {
    if (
        feed.upstreamEnabled &&
        feed.upstreamSource != null &&
        feed.upstreamSource.name !== UpstreamSource_All.name &&
        feed.upstreamSource.name !== UpstreamSource_Local.name
    ) {
        return feed.upstreamSource.protocol;
    }

    return null;
}
